/**
 * Phase B — LocalStorage allow-list.
 *
 * Critical business data must live in the database (app_data / store_tenant_data).
 * LocalStorage is permitted only as:
 *   • a write-through cache for SYNC_KEYS (handled by cloudSync.ts), or
 *   • intentionally local-only state listed below (UI prefs, drafts, recent views,
 *     theme preview, swipe state, per-visitor cart/favorites).
 *
 * Anything outside both lists triggers a dev-time warning so we catch new keys
 * that try to use LS as a source of truth.
 */

/** Keys (or key prefixes ending in '*') allowed to live only in localStorage. */
export const LOCAL_ONLY_KEYS: ReadonlyArray<string> = [
  // Per-visitor commerce state (NOT platform data — owned by the visitor)
  "khays_cart",
  "khays_favorites",
  "khays_lite_cart",
  "khays_lite_favorites",
  "khays_lite_user_*",
  "khays_recent_devices",
  "khays_storefront_session",

  // UI preferences / one-device toggles
  "khays_theme_preview",
  "khays_admin_sidebar_collapsed",
  "khays_admin_active_tab",
  "khays_storefront_view_mode",
  "khays_last_role_context",
  "khays_login_prompt_pref",
  "khays_otp_challenge",
  "khays_otp_device_id",

  // Drafts (user is mid-edit; not committed yet)
  "khays_order_form_draft",
  "khays_review_draft",
  "khays_register_draft_*",
  "khays_swap_form_draft",
  "khays_bg_editor_*",
  "khays_background_presets_local",

  // Per-user baselines (not platform settings)
  "khays_last_notif_check",
  "khays_last_notif_check:*",

  // Cross-tab session isolation guards
  "khays_active_session",
  "khays_session_owner",

  // Supabase SDK (managed by @supabase/supabase-js)
  "sb-*",
] as const;

const ALLOW_PREFIXES = LOCAL_ONLY_KEYS.filter(k => k.endsWith("*")).map(k => k.slice(0, -1));
const ALLOW_EXACT = new Set(LOCAL_ONLY_KEYS.filter(k => !k.endsWith("*")));

export function isLocalOnlyKeyAllowed(key: string): boolean {
  if (ALLOW_EXACT.has(key)) return true;
  return ALLOW_PREFIXES.some(p => key.startsWith(p));
}

/** Dev-time guard. Call at boot to catch accidental LS-only platform data. */
export function auditLocalStorageUsage(syncKeys: ReadonlyArray<string>): string[] {
  if (typeof window === "undefined" || typeof localStorage === "undefined") return [];
  const sync = new Set(syncKeys);
  const offenders: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (!k.startsWith("khays_") && !k.startsWith("sb-")) continue;
      if (sync.has(k)) continue;
      if (isLocalOnlyKeyAllowed(k)) continue;
      offenders.push(k);
    }
  } catch { /* noop */ }
  if (offenders.length && import.meta.env?.DEV) {
    console.warn(
      "[localOnlyKeys] These localStorage keys are neither in SYNC_KEYS nor in the local-only allow-list:",
      offenders,
      "— either add them to cloudSync SYNC_KEYS (server-persisted) or to LOCAL_ONLY_KEYS in src/lib/localOnlyKeys.ts.",
    );
  }
  return offenders;
}
